import { Uuid } from "@/domains/global/identifiers";
import { api } from "@/modules/api";
import { useQuery } from "@tanstack/react-query";
import { FC } from "react";
import { useNavigate, useParams } from "react-router";
import { Button, Flex, Badge, Card, Heading, Text, Box, Separator } from "@radix-ui/themes";
import { atlasRoutes } from "@/app/router/routes";
import { DateTime } from "luxon";
import styled from "@emotion/styled";
import Lowlight from "react-lowlight";

export const AssistantThreadsViewPage: FC = () => {
  const navigate = useNavigate();
  const { threadId } = useParams<{ threadId: Uuid }>();

  if (!threadId) {
    return <div>Invalid Thread ID</div>;
  }

  const result = useQuery({
    queryKey: ["assistant-thread", threadId],
    queryFn: () =>
      api.get("/internal/v1/assistant-threads/{thread_id}", {
        params: {
          path: {
            thread_id: threadId,
          },
        },
      }),
    staleTime: DateTime.fromObject({ minute: 1 }).toMillis(),
  });

  if (result.isLoading) {
    return <div>Loading...</div>;
  }

  if (result.isError || !result.data?.data) {
    return <div>Error: {result.error?.message ?? "Unknown error"}</div>;
  }

  const thread = result.data.data;

  // Create lookup maps for tasks and reminders
  const tasksById = new Map(thread.tasks?.map(t => [t.id, t]) ?? []);
  const remindersById = new Map(thread.reminders?.map(r => [r.id, r]) ?? []);
  const remindersByTaskId = new Map<string, typeof thread.reminders>();
  const unlinkedReminders: typeof thread.reminders = [];
  thread.reminders?.forEach(reminder => {
    const taskId = reminder.associated_task_id;
    if (taskId) {
      const existing = remindersByTaskId.get(taskId) ?? [];
      remindersByTaskId.set(taskId, [...existing, reminder]);
    } else {
      unlinkedReminders.push(reminder);
    }
  });

  // Enrich activity content with task/reminder details
  const getEnrichedActivityContent = (activity: (typeof thread.activities)[number]) => {
    const content = { ...activity.content } as Record<string, unknown>;
    const activityType = activity.activity_type;

    // Enrich task-related activities
    if (activityType.includes("task") && content.task_id) {
      const task = tasksById.get(content.task_id as string);
      if (task) {
        content.task_title = task.title;
        if (task.description) {
          content.task_description = task.description;
        }
        content.task_status = task.status;
      }
    }

    // Enrich reminder-related activities
    if (activityType.includes("reminder") && content.reminder_id) {
      const reminder = remindersById.get(content.reminder_id as string);
      if (reminder) {
        content.reminder_content = reminder.content;
        content.reminder_status = reminder.status;
        content.reminder_scheduled_for = reminder.scheduled_for;
      }
    }

    return content;
  };

  const getStatusColor = (status: string): "green" | "orange" | "gray" | "blue" => {
    switch (status) {
      case "active":
        return "green";
      case "resolved":
        return "blue";
      case "archived":
        return "gray";
      default:
        return "orange";
    }
  };

  const getTaskStatusColor = (status: string): "green" | "orange" | "gray" | "blue" | "yellow" => {
    switch (status) {
      case "completed":
        return "green";
      case "in_progress":
        return "blue";
      case "pending":
        return "yellow";
      case "canceled":
        return "gray";
      default:
        return "orange";
    }
  };

  const getReminderStatusColor = (
    status: string
  ): "green" | "orange" | "gray" | "blue" | "yellow" => {
    switch (status) {
      case "completed":
        return "green";
      case "fired":
        return "blue";
      case "pending":
        return "yellow";
      case "canceled":
        return "gray";
      default:
        return "orange";
    }
  };

  return (
    <Container>
      <br />
      <Flex gap="3" align="center">
        <Button
          variant="outline"
          onClick={() =>
            navigate(atlasRoutes.assistantsView({ params: { sessionId: thread.session_id } }).path)
          }
        >
          Back to Threads
        </Button>
        <Button
          variant="outline"
          onClick={() =>
            navigate(atlasRoutes.sessionsView({ params: { sessionId: thread.session_id } }).path)
          }
        >
          View Session
        </Button>
      </Flex>

      <br />
      <Flex gap="3" align="center">
        <Heading size="6">{thread.title}</Heading>
        <Badge color={getStatusColor(thread.status)} size="2">
          {thread.status}
        </Badge>
      </Flex>

      <Box mt="3">
        <Text size="2" color="gray">
          Thread ID: {thread.id}
        </Text>
        <br />
        <Text size="2" color="gray">
          Session ID:{" "}
          <a
            href="#"
            onClick={e => {
              e.preventDefault();
              navigate(atlasRoutes.sessionsView({ params: { sessionId: thread.session_id } }).path);
            }}
          >
            {thread.session_id}
          </a>
        </Text>
        <br />
        <Text size="2" color="gray">
          Created: {DateTime.fromISO(thread.created_at).toLocaleString(DateTime.DATETIME_FULL)}
        </Text>
        <br />
        <Text size="2" color="gray">
          Last Activity: {DateTime.fromISO(thread.last_activity_at).toRelative()}
        </Text>
      </Box>

      {Object.keys(thread.metadata).length > 0 && (
        <Box mt="4">
          <Heading size="4">Metadata</Heading>
          <Lowlight language="json" value={JSON.stringify(thread.metadata, null, 2)} markers={[]} />
        </Box>
      )}

      <Separator size="4" my="5" />

      <Section>
        <Heading size="4">Tasks ({thread.tasks?.length ?? 0})</Heading>
        {thread.tasks?.length === 0 ? (
          <Text color="gray">No tasks</Text>
        ) : (
          <CardList>
            {thread.tasks?.map(task => {
              const taskReminders = remindersByTaskId.get(task.id) ?? [];
              return (
                <Card key={task.id}>
                  <Flex justify="between" align="start">
                    <Box>
                      <Text weight="bold">{task.title}</Text>
                      {task.description && (
                        <Text as="p" size="2" color="gray">
                          {task.description}
                        </Text>
                      )}
                    </Box>
                    <Badge color={getTaskStatusColor(task.status)}>{task.status}</Badge>
                  </Flex>
                  <Flex gap="3" mt="2">
                    <Text size="1" color="gray">
                      Created: {DateTime.fromISO(task.created_at).toRelative()}
                    </Text>
                    {task.due_at && (
                      <Text size="1" color="gray">
                        Due: {DateTime.fromISO(task.due_at).toLocaleString(DateTime.DATETIME_SHORT)}
                      </Text>
                    )}
                    {task.completed_at && (
                      <Text size="1" color="gray">
                        Completed:{" "}
                        {DateTime.fromISO(task.completed_at).toLocaleString(
                          DateTime.DATETIME_SHORT
                        )}
                      </Text>
                    )}
                  </Flex>
                  {Object.keys(task.metadata).length > 0 && (
                    <Box mt="2">
                      <Lowlight
                        language="json"
                        value={JSON.stringify(task.metadata, null, 2)}
                        markers={[]}
                      />
                    </Box>
                  )}

                  <NestedSection>
                    <Text size="2" weight="bold">
                      Reminders ({taskReminders.length})
                    </Text>
                    {taskReminders.length === 0 ? (
                      <Text size="1" color="gray">
                        No reminders
                      </Text>
                    ) : (
                      <NestedList>
                        {taskReminders.map(reminder => (
                          <NestedCard key={reminder.id}>
                            <Flex justify="between" align="start">
                              <Text size="2">{reminder.content}</Text>
                              <Badge color={getReminderStatusColor(reminder.status)}>
                                {reminder.status}
                              </Badge>
                            </Flex>
                            <Flex gap="3" mt="2">
                              <Text size="1" color="gray">
                                Scheduled:{" "}
                                {DateTime.fromISO(reminder.scheduled_for).toLocaleString(
                                  DateTime.DATETIME_FULL
                                )}
                              </Text>
                              {reminder.fired_at && (
                                <Text size="1" color="gray">
                                  Fired:{" "}
                                  {DateTime.fromISO(reminder.fired_at).toLocaleString(
                                    DateTime.DATETIME_SHORT
                                  )}
                                </Text>
                              )}
                              {reminder.completed_at && (
                                <Text size="1" color="gray">
                                  Completed:{" "}
                                  {DateTime.fromISO(reminder.completed_at).toLocaleString(
                                    DateTime.DATETIME_SHORT
                                  )}
                                </Text>
                              )}
                            </Flex>
                          </NestedCard>
                        ))}
                      </NestedList>
                    )}
                  </NestedSection>
                </Card>
              );
            })}
          </CardList>
        )}
      </Section>

      {unlinkedReminders.length > 0 && (
        <>
          <Separator size="4" my="5" />
          <Section>
            <Heading size="4">Unlinked Reminders ({unlinkedReminders.length})</Heading>
            <CardList>
              {unlinkedReminders.map(reminder => (
                <Card key={reminder.id}>
                  <Flex justify="between" align="start">
                    <Text>{reminder.content}</Text>
                    <Badge color={getReminderStatusColor(reminder.status)}>{reminder.status}</Badge>
                  </Flex>
                  <Flex gap="3" mt="2">
                    <Text size="1" color="gray">
                      Scheduled:{" "}
                      {DateTime.fromISO(reminder.scheduled_for).toLocaleString(
                        DateTime.DATETIME_FULL
                      )}
                    </Text>
                  </Flex>
                </Card>
              ))}
            </CardList>
          </Section>
        </>
      )}

      <Separator size="4" my="5" />

      <Section>
        <Heading size="4">Activities ({thread.activities?.length ?? 0})</Heading>
        {thread.activities?.length === 0 ? (
          <Text color="gray">No activities</Text>
        ) : (
          <CardList>
            {thread.activities?.map(activity => (
              <Card key={activity.id}>
                <Flex justify="between" align="start">
                  <Flex gap="2" align="center">
                    <Badge variant="outline">{activity.activity_type}</Badge>
                    <Badge variant="soft">{activity.actor}</Badge>
                  </Flex>
                  <Text size="1" color="gray">
                    {DateTime.fromISO(activity.created_at).toLocaleString(DateTime.DATETIME_FULL)}
                  </Text>
                </Flex>
                {Object.keys(activity.content).length > 0 && (
                  <Box mt="2">
                    <Lowlight
                      language="json"
                      value={JSON.stringify(getEnrichedActivityContent(activity), null, 2)}
                      markers={[]}
                    />
                  </Box>
                )}
                {Object.keys(activity.metadata).length > 0 && (
                  <Box mt="2">
                    <Text size="1" color="gray">
                      Metadata:
                    </Text>
                    <Lowlight
                      language="json"
                      value={JSON.stringify(activity.metadata, null, 2)}
                      markers={[]}
                    />
                  </Box>
                )}
              </Card>
            ))}
          </CardList>
        )}
      </Section>
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
  padding: 20px;
`;

const Section = styled.div`
  margin-top: 16px;
`;

const CardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 12px;
`;

const NestedSection = styled.div`
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px dashed var(--gray-a5);
`;

const NestedList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 8px;
`;

const NestedCard = styled.div`
  padding: 10px;
  border-radius: 6px;
  background: var(--gray-a2);
  border: 1px solid var(--gray-a4);
`;
